<template>
  <div class="funnel">
    <div class="funnel-heading">
      <h1 class="funnel-heading-title">Воронка продажів</h1>
    </div>

    <div class="funnel-filters">
      <el-date-picker
        v-model="period"
        type="daterange"
        unlink-panels
        range-separator="до"
        start-placeholder="Початок"
        end-placeholder="Кінець"
        format="DD.MM.YYYY"
        :clearable="false"
        @change="load"
      />
      <el-select
        v-model="widgetCode"
        clearable
        filterable
        placeholder="Усі віджети"
        class="funnel-widgets"
        @change="load"
      >
        <el-option
          v-for="widget in widgets"
          :key="widget.widget_code"
          :label="widgetLabel(widget)"
          :value="widget.widget_code"
        />
      </el-select>
      <el-button type="primary" :loading="loading" @click="load">Оновити</el-button>
    </div>

    <p class="funnel-hint">
      Враховано заявки віджетів ОСЦПВ, ДЦВ, Майно та Туризм, сесія яких розпочалася за обраний період.
      Кожна заявка рахується на найдальшому етапі, якого вона досягла, і на всіх попередніх.
      Етапи до оформлення (відкриття віджета, розрахунок, страхувальник) в історичних даних не зберігаються.
    </p>

    <FunnelChart :steps="stepRows" v-loading="loading" />

    <el-table
      :data="stepRows"
      :border="true"
      v-loading="loading"
      style="font-family: 'Open Sans', sans-serif"
      class="table funnel-table"
    >
      <el-table-column label="Етап" min-width="260">
        <template #default="scope">{{ scope.row.title }}</template>
      </el-table-column>
      <el-table-column label="К-ть користувачів" min-width="150" align="right">
        <template #default="scope">{{ formatNumber(scope.row.users) }}</template>
      </el-table-column>
      <el-table-column label="% від попереднього етапу" min-width="200" align="right">
        <template #default="scope">{{ scope.row.fromPrevious }}</template>
      </el-table-column>
      <el-table-column label="Відтік (к-ть / %)" min-width="170" align="right">
        <template #default="scope">{{ scope.row.dropOff }}</template>
      </el-table-column>
      <el-table-column label="% від початку" min-width="130" align="right">
        <template #default="scope">{{ scope.row.fromStart }}</template>
      </el-table-column>
    </el-table>

    <div class="funnel-amount">
      Сума оплачених: <b>{{ formatMoney(paidAmount) }}</b>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { ElMessage } from "element-plus";
import { fetchFunnelReport } from "../services";
import FunnelChart from "../components/FunnelChart.vue";

// Steps 4-7 of the customer's funnel; steps 1-3 are not kept in the historical data
const STEPS = [
  { key: "step_4", title: "Натиснули «Оформити»" },
  { key: "step_5", title: "OTP" },
  { key: "step_6", title: "Підписали договір" },
  { key: "step_7", title: "Оплатили" },
];

const TEMPLATE_NAMES = { ocv: "ОСЦПВ", dcv: "ДЦВ", house: "Майно", tourism: "Туризм" };

export default {
  name: "Funnel",
  components: { FunnelChart },
  data() {
    return {
      period: [dayjs().subtract(30, "day").toDate(), dayjs().toDate()],
      widgetCode: "",
      widgets: [],
      rows: [],
      loading: false,
    };
  },
  computed: {
    counts() {
      return STEPS.map(({ key }) => this.rows.reduce((sum, row) => sum + Number(row[key] || 0), 0));
    },
    paidAmount() {
      return this.rows.reduce((sum, row) => sum + Number(row.paid_amount || 0), 0);
    },
    stepRows() {
      const [start] = this.counts;
      return STEPS.map(({ title }, index) => {
        const users = this.counts[index];
        if (index === 0) {
          return { title, users, fromStart: start ? "100%" : "—", fromPrevious: "—", dropOff: "—" };
        }
        const previous = this.counts[index - 1];
        // Drop-off: reached the previous step but not this one
        const lost = previous - users;
        return {
          title,
          users,
          fromStart: this.percent(users, start),
          fromPrevious: this.percent(users, previous),
          dropOff: previous ? `${this.formatNumber(lost)} (${this.percent(lost, previous)})` : "—",
        };
      });
    },
  },
  methods: {
    async load() {
      const [from, to] = this.period || [];
      this.loading = true;
      try {
        const respond = await fetchFunnelReport({
          date_from: dayjs(from).format("YYYY-MM-DD"),
          date_to: dayjs(to).format("YYYY-MM-DD"),
          widget_code: this.widgetCode || "",
        });
        if (respond.status_code !== "200") throw new Error(respond.status_msg);
        this.rows = respond.data.data_set || [];
        this.widgets = respond.data.widgets || [];
      } catch (error) {
        this.rows = [];
        ElMessage({ message: "Не вдалося завантажити воронку", type: "error" });
      } finally {
        this.loading = false;
      }
    },
    widgetLabel(widget) {
      const product = TEMPLATE_NAMES[widget.widget_template] || widget.widget_template;
      const name = widget.widget_name || widget.widget_code;
      return `${product}: ${name} (${widget.widget_code})`;
    },
    percent(value, base) {
      const number = Number(value || 0);
      const total = Number(base || 0);
      return total ? `${((number / total) * 100).toFixed(1)}%` : "—";
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("uk-UA");
    },
    formatMoney(value) {
      return `${Number(value || 0).toLocaleString("uk-UA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} грн`;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.funnel {
  margin: 20px 20px 0 20px;
}
.funnel-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}
.funnel-heading-title {
  font-weight: 600;
  font-size: 20px;
  font-family: Raleway;
}
.funnel-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}
.funnel-widgets {
  min-width: 360px;
}
.funnel-hint {
  margin: 12px 0 24px;
  color: #6b6e75;
  font-size: 13px;
  max-width: 900px;
}
.funnel-table {
  max-width: 1100px;
}
.funnel-amount {
  margin-top: 16px;
}
</style>
